import useAuth from '@components/_hooks/useAuth';
import Script from 'next/script';
import { useEffect } from 'react';

const AnalyticsScriptInjector = ({ template_settings = {} }) => {
    const {
        ga_measurement_id,
        gtm_id,
        bing_uet_tag,
        enableAutoSpaTracking = true,
    } = template_settings || {};

    const google_conversion = template_settings?.google_conversion == 'yes';
    const google_conversion_id = template_settings?.google_conversion_id ?? '';
    const google_conversion_label = template_settings?.google_conversion_label ?? '';

    const { is_agent, isAuthenticated } = useAuth();
    const analyticsDisabled = (isAuthenticated && is_agent);

    useEffect(() => {
        if (analyticsDisabled) {
            window.__disableAnalytics = true;

            // disable GA for this measurement ID
            if (ga_measurement_id) {
                window[`ga-disable-${ga_measurement_id}`] = true;
            }

            // stop bing
            window.uetq = [];
        } else {
            // disable analytics
            window.__disableAnalytics = false;

            // disable ga
            if (ga_measurement_id) {
                window[`ga-disable-${ga_measurement_id}`] = false;
            }
        }
    }, [analyticsDisabled, ga_measurement_id]);

    return (
        <>
            {ga_measurement_id && (
                <>
                    {/* GA and webworker init */}
                    <script
                        type="text/partytown"
                        src={`https://www.googletagmanager.com/gtag/js?id=${ga_measurement_id}`}
                    />

                    {/* GA */}
                    <script
                        type="text/partytown"
                        dangerouslySetInnerHTML={{
                            __html: `
                                window.dataLayer = window.dataLayer || [];
                                function gtag(){ dataLayer.push(arguments); }
                                window.gtag = window.gtag || gtag;

                                gtag('consent', 'default', {
                                    ad_storage: 'granted',
                                    analytics_storage: 'granted',
                                    wait_for_update: 0
                                });

                                gtag('js', new Date());
                                gtag('config', '${ga_measurement_id}', {
                                    send_page_view: false
                                });

                                //initial page view
                                gtag('event', 'page_view', {
                                    page_title: document.title,
                                    page_location: location.href,
                                    page_path: location.pathname
                                });
                            `,
                        }}
                    />
                </>
            )}

            {/* GTM (stops automatic page pushing as we use GA) */}
            {gtm_id && (
                <>
                    <script
                        type="text/partytown"
                        dangerouslySetInnerHTML={{
                            __html: `
                                // Prevent GTM from firing auto page_view as we already have one
                                window.dataLayer = window.dataLayer || [];
                                const originalPush = window.dataLayer.push;
                                window.dataLayer.push = function() {
                                    const args = Array.from(arguments);
                                    const eventName = args[0]?.event;
                                    if (eventName === 'gtm.js') {
                                        //stops gtm.js event
                                        return;
                                    }
                                    return originalPush.apply(this, args);
                                };

                                // load GTM
                                (function(w,d,s,l,i){
                                    w[l]=w[l]||[];
                                    var f=d.getElementsByTagName(s)[0],
                                        j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
                                    j.async=true;
                                    j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
                                    f.parentNode.insertBefore(j,f);
                                })(window,document,'script','dataLayer','${gtm_id}');
                            `,
                        }}
                    />

                    <noscript
                        dangerouslySetInnerHTML={{
                            __html: `
                                <iframe src="https://www.googletagmanager.com/ns.html?id=${gtm_id}"
                                        height="0" width="0" style="display:none;visibility:hidden"></iframe>
                            `,
                        }}
                    />
                </>
            )}

            {/* google ads conversion */}
            {google_conversion && (
                <>
                    <script
                        type="text/partytown"
                        src="https://www.googleadservices.com/pagead/conversion_async.js"
                    />
                    <script
                        type="text/partytown"
                        dangerouslySetInnerHTML={{
                            __html: `
                                (function(){
                                    self.goog_snippet_vars = function() {
                                        var w = self;
                                        w.google_conversion_id = ${Number(google_conversion_id) || 0};
                                        w.google_conversion_label = "${google_conversion_label}";
                                        w.google_remarketing_only = false;
                                    };

                                    self.goog_report_conversion = function(url) {
                                        try {
                                            self.goog_snippet_vars();
                                            self.google_conversion_format = "3";
                                            var opt = {};
                                            opt.onload_callback = function(){
                                                if (typeof url !== 'undefined') self.location.href = url;
                                            };
                                            var conv_handler = self['google_trackConversion'];
                                            if (typeof conv_handler === 'function') conv_handler(opt);
                                            else (self.__pt_convq = self.__pt_convq || []).push(opt);
                                        } catch(e){}
                                    };

                                    Object.defineProperty(self, 'google_trackConversion', {
                                        configurable: true,
                                        set: function(fn){
                                            this.__gtc = fn;
                                            var q = self.__pt_convq || [];
                                            for (var i=0;i<q.length;i++){ try{ fn(q[i]); }catch(e){} }
                                            self.__pt_convq = [];
                                        },
                                        get: function(){ return this.__gtc; }
                                    });
                                })();
                            `,
                        }}
                    />
                </>
            )}

            {/* bing uet tag */}
            {bing_uet_tag && (
                <>
                    <Script id="bing-uet-q" strategy="beforeInteractive">
                        {`window.uetq = window.uetq || [];`}
                    </Script>

                    <Script
                        id="bing-uet-lib"
                        src="https://bat.bing.com/bat.js"
                        strategy="afterInteractive"
                        onLoad={() => {
                            try {
                                if (typeof navigator !== 'undefined' && navigator.webdriver) return;
                                if (window.__uetInitialized) return;

                                if (typeof window.UET === 'function') {
                                    var opts = { ti: bing_uet_tag, enableAutoSpaTracking: !!enableAutoSpaTracking };
                                    window.uetq = new window.UET(opts);
                                    window.uetq.push('pageLoad');
                                    window.__uetInitialized = true;
                                } else {
                                    // If blocked by ad blocker, init so it doesnt break
                                    window.uetq = window.uetq || [];
                                }
                            } catch (e) {
                                console.log(e)
                                // Do nothing
                            }
                        }}
                        onError={() => {
                            window.uetq = window.uetq || [];
                        }}
                    />

                    <noscript
                        dangerouslySetInnerHTML={{
                            __html: `
                                <img src="//bat.bing.com/action/0?ti=${bing_uet_tag}&Ver=2"
                                     height="0" width="0" style="display:none; visibility: hidden;" alt="" />
                            `,
                        }}
                    />
                </>
            )}
        </>
    );
};

export default AnalyticsScriptInjector;
